import React, { useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import {
  CrewPilotNavList,
  CrewPilotNavRow,
  CrewPilotNavSectionHeader,
} from "../CrewPilotNavRow";
import { usePermissions } from "../../hooks/usePermissions";
import {
  deleteEstimateLocation,
  formatLocationLabel,
  workTypeLabel,
} from "../../helpers/estimates";
import {
  computeLocationTotal,
  computeWorkTypeTotal,
  computeWorkTypeTotalSqft,
  formatCurrency,
} from "../../helpers/estimateTotals";
import { AddFeatureScreen } from "./AddFeatureScreen";
import { EstimateOverflowMenu } from "./EstimateOverflowMenu";
import { LocationNameSheet } from "./LocationNameSheet";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { LocationWorkspaceDesktop } from "./desktop/LocationWorkspaceDesktop";
import "./estimateWorkspace.css";

function featureSubtitle(workType) {
  const sqft = computeWorkTypeTotalSqft(workType);
  return sqft > 0 ? `${sqft.toFixed(0)} ft²` : null;
}

/**
 * SCREEN 2 — Location Workspace.
 * Features as compact navigable rows; "+ Add Feature" opens the dedicated
 * Add Feature screen (a workflow choice, not small data entry) which
 * preserves the exact three creation mechanisms. Rename/Delete moved into a
 * compact overflow menu instead of two permanent primary-looking buttons.
 */
export function LocationWorkspaceScreen({
  location,
  featureTypes,
  workTypeTemplates = [],
  materialsCatalog,
  organizationId = null,
  editable,
  onReload,
  onTemplatesChanged,
  onBack,
  onOpenFeature,
  onLocationDeleted,
  parentLabel,
}) {
  const isDesktop = useIsDesktopViewport();
  const { actor } = usePermissions();
  const [showAddFeature, setShowAddFeature] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const workTypes = location.workTypes || [];
  const locationTotal = computeLocationTotal(location, materialsCatalog);
  const locationName = formatLocationLabel(location);

  async function handleDeleteLocation() {
    if (!editable || location.is_default_scope) return;
    if (!window.confirm(`Delete location "${locationName}"?`)) {
      return;
    }
    setDeleting(true);
    setError("");
    try {
      await deleteEstimateLocation(actor, location.room_id);
      await onLocationDeleted?.();
    } catch (err) {
      console.error("[LocationWorkspaceScreen] Delete failed:", err);
      setError(err.message || "Unable to delete location.");
    } finally {
      setDeleting(false);
    }
  }

  if (showAddFeature) {
    return (
      <AddFeatureScreen
        locationName={locationName}
        roomId={location.room_id}
        featureTypes={featureTypes}
        workTypeTemplates={workTypeTemplates}
        organizationId={organizationId}
        onBack={() => setShowAddFeature(false)}
        onCreated={onReload}
      />
    );
  }

  const menuItems = editable
    ? [
        { label: "Rename Location", onClick: () => setShowRename(true) },
        !location.is_default_scope && {
          label: deleting ? "Deleting…" : "Delete Location",
          onClick: handleDeleteLocation,
          destructive: true,
          disabled: deleting,
        },
      ]
    : [];

  if (isDesktop) {
    return (
      <LocationWorkspaceDesktop
        name={locationName}
        parentLabel={parentLabel}
        jobWide={location.is_default_scope === true}
        error={error}
        rows={workTypes.map((workType) => ({
          id: workType.estimate_feature_id,
          name: workTypeLabel(workType),
          sqft: computeWorkTypeTotalSqft(workType),
          total: computeWorkTypeTotal(workType, materialsCatalog),
          materialsCount: (workType.materials || []).length,
          labourCount: (workType.labour || []).length,
        }))}
        total={locationTotal}
        editable={editable}
        menuItems={menuItems.filter(Boolean)}
        onBack={onBack}
        onOpenFeature={onOpenFeature}
        onAddFeature={() => setShowAddFeature(true)}
      >
        {showRename && (
          <LocationNameSheet
            location={location}
            onClose={() => setShowRename(false)}
            onSaved={onReload}
          />
        )}
      </LocationWorkspaceDesktop>
    );
  }

  return (
    <CrewPilotModuleShell title={locationName} sectionLabel="Location" onBack={onBack}>
      {error && <div className="est-inline-error">{error}</div>}

      <div className="est-workspace-header-row">
        <div className="est-workspace-header-text est-page-subtitle-row">
          <span>
            {workTypes.length} feature{workTypes.length === 1 ? "" : "s"}
          </span>
          {locationTotal > 0 && (
            <>
              <span className="est-page-subtitle-dot">·</span>
              <span>{formatCurrency(locationTotal)}</span>
            </>
          )}
        </div>
        <EstimateOverflowMenu items={menuItems} ariaLabel="Location actions" />
      </div>

      {location.is_default_scope && (
        <div className="est-home-empty-row" style={{ marginBottom: 16 }}>
          Job-wide scope for mobilization, permits, and other work not tied to
          a specific area.
        </div>
      )}

      <CrewPilotNavSectionHeader>Features</CrewPilotNavSectionHeader>

      {workTypes.length === 0 ? (
        editable ? (
          <button
            type="button"
            className="est-add-first-btn"
            onClick={() => setShowAddFeature(true)}
          >
            + Add First Feature
          </button>
        ) : (
          <div className="est-home-empty-row">No features yet.</div>
        )
      ) : (
        <>
          <CrewPilotNavList>
            {workTypes.map((workType) => (
              <CrewPilotNavRow
                key={workType.estimate_feature_id}
                title={workTypeLabel(workType)}
                subtitle={featureSubtitle(workType)}
                trailingText={
                  computeWorkTypeTotal(workType, materialsCatalog) > 0
                    ? formatCurrency(computeWorkTypeTotal(workType, materialsCatalog))
                    : null
                }
                onClick={() => onOpenFeature(workType.estimate_feature_id)}
              />
            ))}
          </CrewPilotNavList>

          {editable && (
            <button
              type="button"
              className="est-add-row-btn"
              onClick={() => setShowAddFeature(true)}
            >
              + Add Feature
            </button>
          )}
        </>
      )}

      <div className="est-bottom-spacer" />

      {showRename && (
        <LocationNameSheet
          location={location}
          onClose={() => setShowRename(false)}
          onSaved={onReload}
        />
      )}
    </CrewPilotModuleShell>
  );
}
